import React, { type ElementRef, forwardRef, useImperativeHandle, useRef, useState } from 'react';
import {
  Platform,
  TextInput as RnTextInput,
  TextInputProps as RnTextInputProps,
  StyleProp,
  StyleSheet,
  TextStyle,
  ViewStyle,
} from 'react-native';

import { Text } from '@fhs/ui';
import { useMergedEventHandlers } from '@fhs/utils';

import { tokens } from '../..//tokens';

import { InputContainer } from './input-container';

export type FormControlTextInputProps = Omit<
  RnTextInputProps,
  'editable' | 'placeholderTextColor'
> & {
  readOnly?: boolean;
  inputContainerStyle?: StyleProp<ViewStyle>;
  labelStyle?: StyleProp<TextStyle>;
  focusedStyle?: RnTextInputProps['style'];
  hoveredStyle?: RnTextInputProps['style'];
  readOnlyStyle?: RnTextInputProps['style'];
  leftIcon?: React.JSX.Element;
  rightIcon?: React.JSX.Element;
  label?: string;
  error?: string | boolean;
  required?: boolean;
};

export const FormControlTextInput = forwardRef<RnTextInput, FormControlTextInputProps>(
  function FormControlTextInput(
    { label, leftIcon, rightIcon, inputContainerStyle, labelStyle, required, ...props },
    forwardedRef
  ) {
    const inputRef = useRef<ElementRef<typeof RnTextInput> | null>(null);
    const [focused, setFocused] = useState(false);
    const [hovered, setHovered] = useState(false);

    const handleOnFocus = useMergedEventHandlers(props.onFocus, () => setFocused(true));
    const handleOnBlur = useMergedEventHandlers(props.onBlur, () => setFocused(false));

    useImperativeHandle(forwardedRef, () => inputRef.current!);

    return (
      <>
        {label && (
          <Text weight="semibold" style={[styles.label, labelStyle]}>
            {label}
            {required && <Text style={styles.required}> *</Text>}
          </Text>
        )}
        <InputContainer
          style={
            [
              styles.inputContainer,
              inputContainerStyle,
              hovered && [styles.hovered, props.hoveredStyle],
              focused && [styles.focused, props.focusedStyle],
              props.readOnly && [styles.readOnly, props.readOnlyStyle],
              Boolean(props.error) && [styles.error],
              props.style,
            ] as ViewStyle[]
          }
          onHoverIn={() => setHovered(true)}
          onHoverOut={() => setHovered(false)}
        >
          {leftIcon && leftIcon}
          <RnTextInput
            {...props}
            readOnly={props.readOnly}
            onFocus={handleOnFocus}
            onBlur={handleOnBlur}
            placeholderTextColor={tokens.colors.$placeholderText}
            style={[styles.baseline, props.style]}
            ref={inputRef}
          />
          {rightIcon && rightIcon}
        </InputContainer>
        {props.error && <Text style={styles.errorMessage}>{props.error}</Text>}
      </>
    );
  }
);

const styles = StyleSheet.create({
  required: {
    color: tokens.colors.$error,
    fontSize: 14,
    fontWeight: 'bold',
    height: 16,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: tokens.colors.$blackOpacity30,
    backgroundColor: tokens.colors.$white,
    minHeight: 48,
    paddingHorizontal: 16,
    flex: 1, // TextInput takes available space
  },
  baseline: {
    fontFamily: 'Montserrat',
    fontSize: 16,
    lineHeight: 20,
    color: tokens.colors.$houseDark,
    paddingHorizontal: 6,
    flex: 1, // TextInput takes available space
    ...Platform.select({
      web: { outlineWidth: 0, outlineColor: 'transparent' }, // remove outline on web
    }),
  },

  pressed: {
    borderColor: tokens.colors.$houseDark,
  },

  focused: {
    borderColor: tokens.colors.$houseDark,
  },

  hovered: {
    backgroundColor: tokens.colors.$blackOpacity04,
  },
  label: {
    marginBottom: 4,
  },
  readOnly: {
    color: tokens.colors.$disabledText,
    borderColor: tokens.colors.$blackOpacity30,
    backgroundColor: tokens.colors.$blackOpacity04,
    // web allows this, but react native changed their types
    ...(Platform.select({ web: { cursor: 'not-allowed' } }) as any),
  },
  error: {
    borderColor: tokens.colors.$error,
  },
  errorMessage: {
    color: tokens.colors.$error,
    fontSize: 12,
    height: 16,
  },
});
